/**
 * Copyright schukai GmbH and contributors 2023. All Rights Reserved.
 * Node module: @schukai/monster
 * This file is licensed under the AGPLv3 License.
 * License text available at https://www.gnu.org/licenses/agpl-3.0.en.html
 */

import { Base } from "./base.mjs";
import { validateString } from "./validate.mjs";

export { ID };

/**
 * @private
 * @type {Map<string, integer>}
 */
const internalCounter = new Map();

/**
 * With the id class, sequences of ids can be created. for this purpose, an internal counter is incremented for each prefix.
 * thus, the first id with the prefix `myid` will be `myid1` and the second id `myid2`.
 * The ids are the same for every call, for example on a web page.
 *
 * So the ids can also be used for navigation. you just have to take care that the order stays the same.
 *
 * As of version 1.6.0 there is the new RandomID. this ID class is continuous from now on.
 *
 * @license AGPLv3
 * @since 1.0.0
 * @copyright schukai GmbH
 * @memberOf Monster.Types
 * @summary Automatic generation of ids
 */
class ID extends Base {
	/**
	 * create new id with prefix
	 *
	 * @param {string} prefix
	 */
	constructor(prefix) {
		super();

		if (prefix === undefined) {
			prefix = "id";
		}

		validateString(prefix);

		if (!internalCounter.has(prefix)) {
			internalCounter.set(prefix, 1);
		}

		let count = internalCounter.get(prefix);
		this.id = prefix + count;

		internalCounter.set(prefix, ++count);
	}

	/**
	 * @return {string}
	 */
	toString() {
		return this.id;
	}
}
